import React, { useState, useEffect, useCallback } from 'react';
import { X, Plus, CalendarDays, ArrowRight, Repeat, Pencil } from 'lucide-react';

const API = process.env.REACT_APP_API_URL;
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('auth_token')}` });

const DAYS = [
  { key: 'monday', label: 'Mon' },
  { key: 'tuesday', label: 'Tue' },
  { key: 'wednesday', label: 'Wed' },
  { key: 'thursday', label: 'Thu' },
  { key: 'friday', label: 'Fri' },
  { key: 'saturday', label: 'Sat' },
  { key: 'sunday', label: 'Sun' },
];

const fmt = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const todayStr = () => new Date().toISOString().split('T')[0];

// ─── Tab: Temp Reassignments ────────────────────────────────────────────────
function TempReassignmentsTab({ cleaners, properties }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    sourceCleanerId: '',
    targetCleanerId: '',
    propertyIds: [],
    cleaningFeeOverrides: {}, // { [propId]: string }
    startDate: todayStr(),
    endDate: ''
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/temp-reassignments`, { headers: authHeader() });
      if (r.ok) setList(await r.json());
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const sourceProperties = (() => {
    if (!form.sourceCleanerId) return [];
    const cleaner = cleaners.find(c => c._id === form.sourceCleanerId);
    if (!cleaner) return [];
    return (cleaner.assignedProperties || []).map(ap => {
      const prop = properties.find(p => p._id === (ap.propertyId?._id || ap.propertyId));
      return prop ? { ...prop, assignedDays: ap.assignedDays } : null;
    }).filter(Boolean);
  })();

  const targetCleaners = cleaners.filter(c => c._id !== form.sourceCleanerId);

  const handlePropertyToggle = (propId) => {
    setForm(f => {
      const selected = f.propertyIds.includes(propId);
      const newIds = selected ? f.propertyIds.filter(id => id !== propId) : [...f.propertyIds, propId];
      const newOverrides = { ...f.cleaningFeeOverrides };
      if (selected) delete newOverrides[propId];
      return { ...f, propertyIds: newIds, cleaningFeeOverrides: newOverrides };
    });
  };

  const handleFeeOverride = (propId, value) => {
    setForm(f => ({ ...f, cleaningFeeOverrides: { ...f.cleaningFeeOverrides, [propId]: value } }));
  };

  const handleSourceChange = (e) => {
    setForm(f => ({ ...f, sourceCleanerId: e.target.value, propertyIds: [], cleaningFeeOverrides: {}, targetCleanerId: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.sourceCleanerId || !form.targetCleanerId) {
      setError('Please select source and target cleaners.'); return;
    }
    if (!form.propertyIds.length) {
      setError('Please select at least one property.'); return;
    }
    if (!form.startDate || !form.endDate) {
      setError('Please provide start and end dates.'); return;
    }

    // Build overrides array (only include properties with a value set)
    const cleaningFeeOverrides = Object.entries(form.cleaningFeeOverrides)
      .filter(([, v]) => v !== '' && v != null)
      .map(([propertyId, cleaningFee]) => ({ propertyId, cleaningFee: Number(cleaningFee) }));

    setSaving(true);
    try {
      const r = await fetch(`${API}/api/temp-reassignments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify({ ...form, cleaningFeeOverrides })
      });
      const data = await r.json();
      if (!r.ok) { setError(data.message || 'Error saving'); return; }
      setShowForm(false);
      setForm({ sourceCleanerId: '', targetCleanerId: '', propertyIds: [], cleaningFeeOverrides: {}, startDate: todayStr(), endDate: '' });
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this reassignment permanently?')) return;
    try {
      await fetch(`${API}/api/temp-reassignments/${id}`, { method: 'DELETE', headers: authHeader() });
      await load();
    } catch (e) {
      console.error(e);
    }
  };

  const now = new Date();
  const activeList = list.filter(tr => new Date(tr.endDate) >= now);
  const archivedList = list.filter(tr => new Date(tr.endDate) < now);

  const renderItem = (tr, isArchivedItem) => (
    <li key={tr._id} className={`rounded-lg px-3 py-2.5 flex items-start justify-between gap-2 ${isArchivedItem ? 'bg-zinc-800/40 opacity-60' : 'bg-zinc-800'}`}>
      <div className="min-w-0">
        <div className="flex items-center gap-1.5 text-sm font-medium flex-wrap">
          <span className={isArchivedItem ? 'text-zinc-400' : 'text-zinc-200'}>{tr.sourceCleanerName}</span>
          <ArrowRight className="w-3 h-3 text-violet-400 flex-shrink-0" />
          <span className={isArchivedItem ? 'text-zinc-400' : 'text-violet-300'}>{tr.targetCleanerName}</span>
          {isArchivedItem && <span className="text-xs text-zinc-600 ml-1">archived</span>}
        </div>
        <div className="text-xs text-zinc-500 mt-0.5">
          {(tr.propertyNames || []).map((name, i) => {
            const override = (tr.cleaningFeeOverrides || []).find(o => tr.propertyIds[i]?.toString() === o.propertyId?.toString());
            return (
              <span key={i}>
                {i > 0 && ', '}
                {name}
                {override != null && <span className="text-zinc-600 ml-1">(${override.cleaningFee} fee)</span>}
              </span>
            );
          })}
        </div>
        <div className="text-xs text-zinc-600 mt-0.5">{fmt(tr.startDate)} – {fmt(tr.endDate)}</div>
      </div>
      <button
        onClick={() => handleDelete(tr._id)}
        className="text-zinc-600 hover:text-red-400 transition-colors flex-shrink-0 mt-0.5"
        title="Delete permanently"
      >
        <X className="w-4 h-4" />
      </button>
    </li>
  );

  return (
    <div className="space-y-4">
      {loading ? (
        <p className="text-zinc-500 text-sm">Loading...</p>
      ) : activeList.length === 0 && !showForm ? (
        <p className="text-zinc-500 text-sm">No active reassignments.</p>
      ) : (
        <ul className="space-y-2">
          {activeList.map(tr => renderItem(tr, false))}
        </ul>
      )}

      {!showForm && (
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center gap-1.5 text-xs text-violet-400 hover:text-violet-300 transition-colors"
        >
          <Plus className="w-3.5 h-3.5" />
          New reassignment
        </button>
      )}

      {archivedList.length > 0 && (
        <div className="pt-2 border-t border-zinc-800">
          <button
            onClick={() => setShowArchived(s => !s)}
            className="text-xs text-zinc-600 hover:text-zinc-400 transition-colors"
          >
            {showArchived ? 'Hide' : 'Show'} archived ({archivedList.length})
          </button>
          {showArchived && (
            <ul className="space-y-2 mt-2">
              {archivedList.map(tr => renderItem(tr, true))}
            </ul>
          )}
        </div>
      )}

      {showForm && (
        <form onSubmit={handleSubmit} className="bg-zinc-800/60 rounded-lg p-3 space-y-3 border border-zinc-700">
          <p className="text-xs font-medium text-zinc-300 uppercase tracking-wide">New Temporary Reassignment</p>

          {error && <p className="text-xs text-red-400">{error}</p>}

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs text-zinc-400 mb-1">From cleaner</label>
              <select
                value={form.sourceCleanerId}
                onChange={handleSourceChange}
                className="w-full bg-zinc-900 border border-zinc-700 rounded px-2 py-1.5 text-xs text-zinc-200 focus:outline-none focus:border-violet-500"
              >
                <option value="">— select —</option>
                {cleaners.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-zinc-400 mb-1">To cleaner</label>
              <select
                value={form.targetCleanerId}
                onChange={e => setForm(f => ({ ...f, targetCleanerId: e.target.value }))}
                className="w-full bg-zinc-900 border border-zinc-700 rounded px-2 py-1.5 text-xs text-zinc-200 focus:outline-none focus:border-violet-500"
                disabled={!form.sourceCleanerId}
              >
                <option value="">— select —</option>
                {targetCleaners.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
              </select>
            </div>
          </div>

          {sourceProperties.length > 0 && (
            <div>
              <label className="block text-xs text-zinc-400 mb-1">Properties to reassign</label>
              <div className="space-y-2">
                {sourceProperties.map(prop => (
                  <div key={prop._id}>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={form.propertyIds.includes(prop._id)}
                        onChange={() => handlePropertyToggle(prop._id)}
                        className="accent-violet-500"
                      />
                      <span className="text-xs text-zinc-300">{prop.name}</span>
                      {prop.assignedDays?.length > 0 && (
                        <span className="text-xs text-zinc-600">({prop.assignedDays.map(d => d.slice(0, 3)).join(', ')})</span>
                      )}
                    </label>
                    {form.propertyIds.includes(prop._id) && (
                      <div className="mt-1.5 ml-5 flex items-center gap-2">
                        <label className="text-xs text-zinc-500 whitespace-nowrap">Cleaning fee override ($)</label>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={form.cleaningFeeOverrides[prop._id] ?? ''}
                          onChange={e => handleFeeOverride(prop._id, e.target.value)}
                          placeholder="default"
                          className="w-24 bg-zinc-900 border border-zinc-700 rounded px-2 py-1 text-xs text-zinc-200 focus:outline-none focus:border-violet-500"
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs text-zinc-400 mb-1">Start date</label>
              <input
                type="date"
                value={form.startDate}
                onChange={e => setForm(f => ({ ...f, startDate: e.target.value }))}
                className="w-full bg-zinc-900 border border-zinc-700 rounded px-2 py-1.5 text-xs text-zinc-200 focus:outline-none focus:border-violet-500"
              />
            </div>
            <div>
              <label className="block text-xs text-zinc-400 mb-1">End date</label>
              <input
                type="date"
                value={form.endDate}
                min={form.startDate}
                onChange={e => setForm(f => ({ ...f, endDate: e.target.value }))}
                className="w-full bg-zinc-900 border border-zinc-700 rounded px-2 py-1.5 text-xs text-zinc-200 focus:outline-none focus:border-violet-500"
              />
            </div>
          </div>

          <div className="flex gap-2">
            <button
              type="submit"
              disabled={saving}
              className="flex-1 bg-violet-600 hover:bg-violet-500 disabled:opacity-50 text-white text-xs rounded px-3 py-1.5 font-medium transition-colors"
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button
              type="button"
              onClick={() => { setShowForm(false); setError(''); }}
              className="bg-zinc-700 hover:bg-zinc-600 text-zinc-300 text-xs rounded px-3 py-1.5 transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

// ─── Tab: Reservations ────────────────────────────────────────────────────────
const BLANK_FORM = {
  propertyId: '',
  isRecurring: false,
  date: todayStr(),
  daysOfWeek: [],
  startDate: todayStr(),
  endDate: '',
  revenueAmount: '',
  cleaningFee: '',
  notes: ''
};

function ReservationsTab({ properties }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState(BLANK_FORM);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/recurring-reservations`, { headers: authHeader() });
      if (r.ok) setList(await r.json());
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditingId(null); setForm(BLANK_FORM); setError(''); setShowForm(true); };
  const openEdit = (rr) => {
    setEditingId(rr._id);
    const recurring = rr.isRecurring !== false;
    setForm({
      propertyId: rr.propertyId || '',
      isRecurring: recurring,
      date: rr.date ? new Date(rr.date).toISOString().split('T')[0] : '',
      daysOfWeek: rr.daysOfWeek?.length ? rr.daysOfWeek : (rr.dayOfWeek ? [rr.dayOfWeek] : []),
      startDate: rr.startDate ? new Date(rr.startDate).toISOString().split('T')[0] : '',
      endDate: rr.endDate ? new Date(rr.endDate).toISOString().split('T')[0] : '',
      revenueAmount: rr.revenueAmount != null ? String(rr.revenueAmount) : '',
      cleaningFee: rr.cleaningFee != null ? String(rr.cleaningFee) : '',
      notes: rr.notes || ''
    });
    setError('');
    setShowForm(true);
  };
  const closeForm = () => { setShowForm(false); setEditingId(null); setError(''); };

  const toggleDay = (key) => {
    setForm(f => ({
      ...f,
      daysOfWeek: f.daysOfWeek.includes(key)
        ? f.daysOfWeek.filter(d => d !== key)
        : [...f.daysOfWeek, key]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!editingId && !form.propertyId) { setError('Please select a property.'); return; }

    if (form.isRecurring) {
      if (!form.daysOfWeek.length) { setError('Please select at least one day.'); return; }
      if (!form.startDate) { setError('Please set a start date.'); return; }
    } else {
      if (!form.date) { setError('Please set a date.'); return; }
    }

    setSaving(true);
    try {
      const payload = {
        ...form,
        revenueAmount: Number(form.revenueAmount) || 0,
        cleaningFee: Number(form.cleaningFee) || 0,
        endDate: form.isRecurring ? (form.endDate || null) : undefined,
      };
      const url = editingId
        ? `${API}/api/recurring-reservations/${editingId}`
        : `${API}/api/recurring-reservations`;
      const r = await fetch(url, {
        method: editingId ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify(payload)
      });
      const data = await r.json();
      if (!r.ok) { setError(data.message || 'Error saving'); return; }
      closeForm();
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this reservation? Synthetic occurrences will be removed on the next sync.')) return;
    try {
      await fetch(`${API}/api/recurring-reservations/${id}`, { method: 'DELETE', headers: authHeader() });
      await load();
    } catch (e) {
      console.error(e);
    }
  };

  const now = new Date();
  const activeList = list.filter(rr => {
    if (rr.isRecurring === false) return true; // one-time reservations always show as active
    return !rr.endDate || new Date(rr.endDate) >= now;
  });
  const archivedList = list.filter(rr => {
    if (rr.isRecurring === false) return false;
    return rr.endDate && new Date(rr.endDate) < now;
  });

  const renderItem = (rr, isArchived) => {
    const recurring = rr.isRecurring !== false;
    const days = rr.daysOfWeek?.length
      ? rr.daysOfWeek
      : (rr.dayOfWeek ? [rr.dayOfWeek] : []);

    return (
      <li key={rr._id} className={`rounded-lg px-3 py-2.5 flex items-start justify-between gap-2 ${isArchived ? 'bg-zinc-800/40 opacity-60' : 'bg-zinc-800'}`}>
        <div className="min-w-0">
          <div className="flex items-center gap-1.5 text-sm font-medium flex-wrap">
            {recurring
              ? <Repeat className="w-3.5 h-3.5 text-violet-400 flex-shrink-0" />
              : <CalendarDays className="w-3.5 h-3.5 text-zinc-500 flex-shrink-0" />
            }
            <span className={isArchived ? 'text-zinc-400' : 'text-zinc-200'}>{rr.propertyName}</span>
            {isArchived && <span className="text-xs text-zinc-600">archived</span>}
          </div>
          <div className="text-xs text-zinc-400 mt-0.5 capitalize">
            {recurring ? (
              <>
                {days.map(d => d.slice(0, 3)).join(', ')}
                {rr.revenueAmount > 0 && <span className="ml-2">${rr.revenueAmount} rev</span>}
                {rr.cleaningFee > 0 && <span className="ml-2">${rr.cleaningFee} cleaning</span>}
              </>
            ) : (
              <>
                {fmt(rr.date)}
                {rr.revenueAmount > 0 && <span className="ml-2">${rr.revenueAmount} rev</span>}
                {rr.cleaningFee > 0 && <span className="ml-2">${rr.cleaningFee} cleaning</span>}
              </>
            )}
          </div>
          {recurring && (
            <div className="text-xs text-zinc-500 mt-0.5">
              {fmt(rr.startDate)} – {rr.endDate ? fmt(rr.endDate) : 'ongoing'}
            </div>
          )}
          {rr.notes && <div className="text-xs text-zinc-600 mt-0.5 italic">{rr.notes}</div>}
        </div>
        <div className="flex gap-1.5 flex-shrink-0 mt-0.5">
          {!isArchived && (
            <button onClick={() => openEdit(rr)} className="text-zinc-600 hover:text-violet-400 transition-colors" title="Edit">
              <Pencil className="w-3.5 h-3.5" />
            </button>
          )}
          <button onClick={() => handleDelete(rr._id)} className="text-zinc-600 hover:text-red-400 transition-colors" title="Delete permanently">
            <X className="w-4 h-4" />
          </button>
        </div>
      </li>
    );
  };

  return (
    <div className="space-y-4">
      {loading ? (
        <p className="text-zinc-500 text-sm">Loading...</p>
      ) : activeList.length === 0 && !showForm ? (
        <p className="text-zinc-500 text-sm">No reservations.</p>
      ) : (
        <ul className="space-y-2">
          {activeList.map(rr => renderItem(rr, false))}
        </ul>
      )}

      {!showForm && (
        <button onClick={openNew} className="flex items-center gap-1.5 text-xs text-violet-400 hover:text-violet-300 transition-colors">
          <Plus className="w-3.5 h-3.5" />
          New reservation
        </button>
      )}

      {archivedList.length > 0 && !showForm && (
        <div className="pt-2 border-t border-zinc-800">
          <button onClick={() => setShowArchived(s => !s)} className="text-xs text-zinc-600 hover:text-zinc-400 transition-colors">
            {showArchived ? 'Hide' : 'Show'} archived ({archivedList.length})
          </button>
          {showArchived && (
            <ul className="space-y-2 mt-2">
              {archivedList.map(rr => renderItem(rr, true))}
            </ul>
          )}
        </div>
      )}

      {showForm && (
        <form onSubmit={handleSubmit} className="bg-zinc-800/60 rounded-lg p-3 space-y-3 border border-zinc-700">
          <p className="text-xs font-medium text-zinc-300 uppercase tracking-wide">
            {editingId ? 'Edit Reservation' : 'New Reservation'}
          </p>

          {error && <p className="text-xs text-red-400">{error}</p>}

          {/* Property */}
          <div>
            <label className="block text-xs text-zinc-400 mb-1">Property</label>
            <select
              value={form.propertyId}
              onChange={e => setForm(f => ({ ...f, propertyId: e.target.value }))}
              disabled={!!editingId}
              className="w-full bg-zinc-900 border border-zinc-700 rounded px-2 py-1.5 text-xs text-zinc-200 focus:outline-none focus:border-violet-500 disabled:opacity-50"
            >
              <option value="">— select —</option>
              {properties.filter(p => !p.hidden).map(p => <option key={p._id} value={p._id}>{p.name}</option>)}
            </select>
          </div>

          {/* Recurring toggle */}
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={form.isRecurring}
              onChange={e => setForm(f => ({ ...f, isRecurring: e.target.checked }))}
              className="accent-violet-500"
            />
            <span className="text-xs text-zinc-300">Recurring</span>
          </label>

          {/* One-time: single date */}
          {!form.isRecurring && (
            <div>
              <label className="block text-xs text-zinc-400 mb-1">Date</label>
              <input
                type="date"
                value={form.date}
                onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
                className="w-full bg-zinc-900 border border-zinc-700 rounded px-2 py-1.5 text-xs text-zinc-200 focus:outline-none focus:border-violet-500"
              />
            </div>
          )}

          {/* Recurring: days of week + start/end */}
          {form.isRecurring && (
            <>
              <div>
                <label className="block text-xs text-zinc-400 mb-1.5">Days of week</label>
                <div className="flex flex-wrap gap-1.5">
                  {DAYS.map(d => (
                    <button
                      key={d.key}
                      type="button"
                      onClick={() => toggleDay(d.key)}
                      className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${
                        form.daysOfWeek.includes(d.key)
                          ? 'bg-violet-600 text-white'
                          : 'bg-zinc-800 text-zinc-400 hover:text-zinc-200 border border-zinc-700'
                      }`}
                    >
                      {d.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs text-zinc-400 mb-1">Start date</label>
                  <input
                    type="date"
                    value={form.startDate}
                    onChange={e => setForm(f => ({ ...f, startDate: e.target.value }))}
                    className="w-full bg-zinc-900 border border-zinc-700 rounded px-2 py-1.5 text-xs text-zinc-200 focus:outline-none focus:border-violet-500"
                  />
                </div>
                <div>
                  <label className="block text-xs text-zinc-400 mb-1">End date (blank = forever)</label>
                  <input
                    type="date"
                    value={form.endDate}
                    min={form.startDate}
                    onChange={e => setForm(f => ({ ...f, endDate: e.target.value }))}
                    className="w-full bg-zinc-900 border border-zinc-700 rounded px-2 py-1.5 text-xs text-zinc-200 focus:outline-none focus:border-violet-500"
                  />
                </div>
              </div>
            </>
          )}

          {/* Revenue + cleaning fee */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs text-zinc-400 mb-1">
                {form.isRecurring ? 'Revenue / occurrence ($)' : 'Revenue ($)'}
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={form.revenueAmount}
                onChange={e => setForm(f => ({ ...f, revenueAmount: e.target.value }))}
                placeholder="0"
                className="w-full bg-zinc-900 border border-zinc-700 rounded px-2 py-1.5 text-xs text-zinc-200 focus:outline-none focus:border-violet-500"
              />
            </div>
            <div>
              <label className="block text-xs text-zinc-400 mb-1">Cleaning fee ($)</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={form.cleaningFee}
                onChange={e => setForm(f => ({ ...f, cleaningFee: e.target.value }))}
                placeholder="0"
                className="w-full bg-zinc-900 border border-zinc-700 rounded px-2 py-1.5 text-xs text-zinc-200 focus:outline-none focus:border-violet-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs text-zinc-400 mb-1">Notes (optional)</label>
            <input
              type="text"
              value={form.notes}
              onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              placeholder="e.g. Weekly client John"
              className="w-full bg-zinc-900 border border-zinc-700 rounded px-2 py-1.5 text-xs text-zinc-200 focus:outline-none focus:border-violet-500"
            />
          </div>

          <div className="flex gap-2">
            <button
              type="submit"
              disabled={saving}
              className="flex-1 bg-violet-600 hover:bg-violet-500 disabled:opacity-50 text-white text-xs rounded px-3 py-1.5 font-medium transition-colors"
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button
              type="button"
              onClick={closeForm}
              className="bg-zinc-700 hover:bg-zinc-600 text-zinc-300 text-xs rounded px-3 py-1.5 transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

// ─── Main AllotmentsDrawer ─────────────────────────────────────────────────
const TABS = [
  { key: 'temp', label: 'Temp Reassignments', icon: ArrowRight },
  { key: 'reservations', label: 'Reservations', icon: CalendarDays }
];

const AllotmentsDrawer = ({ isOpen, onClose, cleaners = [], properties = [] }) => {
  const [activeTab, setActiveTab] = useState('temp');

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-40" onClick={onClose} />

      <div className="fixed right-0 top-0 h-full w-full max-w-sm bg-zinc-900 border-l border-zinc-800 z-50 flex flex-col shadow-2xl">
        <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-800 flex-shrink-0">
          <div className="flex items-center gap-2">
            <CalendarDays className="w-4 h-4 text-violet-400" />
            <h2 className="text-sm font-semibold text-zinc-200">Manage Allotments</h2>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-300 transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex border-b border-zinc-800 flex-shrink-0">
          {TABS.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 text-xs font-medium transition-colors border-b-2 ${
                  activeTab === tab.key
                    ? 'border-violet-500 text-violet-400'
                    : 'border-transparent text-zinc-500 hover:text-zinc-300'
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                {tab.label}
              </button>
            );
          })}
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {activeTab === 'temp' && (
            <TempReassignmentsTab cleaners={cleaners} properties={properties} />
          )}
          {activeTab === 'reservations' && (
            <ReservationsTab properties={properties} />
          )}
        </div>

        <div className="px-4 py-2.5 border-t border-zinc-800 flex-shrink-0">
          <p className="text-xs text-zinc-600">
            Changes take effect on the next Sync Now or scheduled big-sync.
          </p>
        </div>
      </div>
    </>
  );
};

export default AllotmentsDrawer;
